import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarX, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtDateTime, fmtDateTimeZone, fmtMoney, setPageTitle } from "../lib/format";

const TABS = [
  ["upcoming", "Upcoming"],
  ["active", "Active"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
];

const classify = (b) => {
  const now = Date.now();
  const s = new Date(b.start_time).getTime(), e = new Date(b.end_time).getTime();
  if (["cancelled", "refunded"].includes(b.status)) return "cancelled";
  if (b.status === "paid" && e <= now) return "completed";
  if (["pending", "pending_auth", "pending_payment", "authorized", "paid"].includes(b.status)) {
    if (s <= now && e > now && b.status === "paid") return "active";
    return "upcoming";
  }
  return "upcoming";
};

export default function Bookings() {
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState(null);
  // Tab is URL-driven (?tab=) so dashboard stat cards can deep-link to a filter.
  const [sp, setSp] = useSearchParams();
  const tab = TABS.some(([v]) => v === sp.get("tab")) ? sp.get("tab") : "upcoming";
  const setTab = (v) => setSp(v === "upcoming" ? {} : { tab: v }, { replace: true });

  const load = () => {
    setError(null);
    api.myBookings().then(setBookings).catch((e) => { setError(e.message); setBookings([]); });
  };
  useEffect(() => { setPageTitle("My Bookings"); load(); }, []);

  const filtered = useMemo(() => (bookings || []).filter((b) => classify(b) === tab), [bookings, tab]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="bookings-page">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Driver</p>
        <h1 className="type-display mt-3">My bookings</h1>
        <div className="mt-8 flex gap-2 overflow-auto" role="tablist" aria-label="Booking status">
          {TABS.map(([v, l]) => (
            <button key={v} role="tab" aria-selected={tab === v} data-testid={`bookings-tab-${v}`} onClick={() => setTab(v)}
                    className={`px-4 py-2 rounded-lg text-xs font-semibold border whitespace-nowrap transition-all duration-200 ${tab === v ? "bg-[#E50914] border-[#E50914] text-white" : "bg-transparent border-[#27272A] text-zinc-400 hover:border-[#3F3F46] hover:text-zinc-200"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={load} /> : bookings === null ? <PageLoading label="Loading your bookings…" /> :
            filtered.length === 0 ? (
              <EmptyState icon={<CalendarX className="w-6 h-6 text-[#FF3B45]" />} title={`No ${tab} bookings`}
                body={tab === "upcoming" ? "Find a spot and make your first booking — it'll appear here with your arrival code." : "Bookings will appear here as their status changes."}
                action={tab === "upcoming" ? <Link to="/find" data-testid="bookings-find-cta" className="btn-primary text-sm">Find Parking</Link> : null} />
            ) : (
              <div className="divide-y divide-[#18181B] border-y border-[#18181B]" data-testid="bookings-list">
                {filtered.map((b) => (
                  <Link key={b.booking_id} to={`/bookings/${b.booking_id}`} data-testid={`booking-row-${b.booking_id}`}
                        className="group flex items-center gap-5 py-5 -mx-3 px-3 rounded-lg transition-colors hover:bg-white/[0.02]">
                    <div className="w-20 h-14 rounded-lg bg-[#050607] border border-[#27272A] overflow-hidden shrink-0 flex items-center justify-center text-xl">
                      {b.spot_image ? <img src={b.spot_image} alt="" className="w-full h-full object-cover" loading="lazy" /> : "🅿️"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-display font-semibold truncate group-hover:text-white transition-colors">{b.spot_title}</h3>
                        <StatusBadge value={b.status} />
                      </div>
                      <p className="mt-1 text-xs text-zinc-500 truncate">{b.spot_address}</p>
                      <p className="mt-1.5 text-xs text-zinc-600">{fmtDateTimeZone(b.start_time, b.timezone)} → {fmtDateTimeZone(b.end_time, b.timezone)}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-4 shrink-0">
                      <span className="font-display font-semibold">{fmtMoney(b.total)}</span>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-300 group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
